import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * WorkspaceChromeContext — lets the active project tab inject controls into the
 * subheader's row-2 ends without the shared SubHeaderWithTabs / ProjectHeader
 * having to know about any specific tab.
 *
 * The subheader (ProjectHeader) renders ABOVE the routed <Outlet/>, so a tab
 * that wants a control in the bar (e.g. the TARA workflow rail/panel toggles)
 * can't place it there directly. Instead the tab calls `useSubHeaderSlot(region,
 * node)` to register a node into a named end-slot; ProjectHeader reads the slots
 * and renders them. The state driving the node stays local to the tab.
 *
 * Two regions: `'lead'` (far-left of the tab row) and `'trail'` (far-right).
 *
 * The default context value is a no-op — consumers (and their tests) render
 * fine with no provider mounted; the slots simply stay empty.
 */

const noop = () => {};
const DEFAULT = { lead: null, trail: null, setSlot: noop };

const WorkspaceChromeContext = createContext(DEFAULT);

export function WorkspaceChromeProvider({ children }) {
  const [slots, setSlots] = useState({ lead: null, trail: null });

  // Replace one region's node (or clear it with `null`). Identity-checked so a
  // re-register with the same node doesn't trigger a needless re-render.
  const setSlot = useCallback((region, node) => {
    setSlots((prev) => (prev[region] === node ? prev : { ...prev, [region]: node }));
  }, []);

  const value = useMemo(
    () => ({ lead: slots.lead, trail: slots.trail, setSlot }),
    [slots.lead, slots.trail, setSlot],
  );

  return (
    <WorkspaceChromeContext.Provider value={value}>
      {children}
    </WorkspaceChromeContext.Provider>
  );
}

/** Read the registered slot nodes — used by the subheader to render them. */
export function useWorkspaceChrome() {
  const { lead, trail } = useContext(WorkspaceChromeContext);
  return { lead, trail };
}

/**
 * Register `node` into the named end-slot (`'lead' | 'trail'`) for as long as
 * the calling component is mounted, clearing it on unmount. Pass `deps` so the
 * slot updates when the node's inputs change (e.g. a toggle's `collapsed` flag).
 * Passing `null` as the node registers nothing — handy for conditional controls.
 */
export function useSubHeaderSlot(region, node, deps = []) {
  const { setSlot } = useContext(WorkspaceChromeContext);
  useEffect(() => {
    setSlot(region, node);
    return () => setSlot(region, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, setSlot, ...deps]);
}
